import { Action, Buyer, Case, Note, Policy, Status, TradingCaseFile, TradingQueueRow } from "./types";
import { ReactNode, createContext, useState } from "react";

import { DropdownItemProps } from "semantic-ui-react";
import { notImplemented } from "../../../services/Library";

export interface TradingQueueContextProps {
    buyers: Buyer[] | undefined;
    caseActions: Action[] | undefined;
    caseDetail: Case | undefined;
    casePolicies: Policy[] | undefined;
    companyOptions: DropdownItemProps[] | undefined;
    excelLoading: boolean;
    isBidModalOpen: boolean;
    isCaseActionsModalOpen: boolean;
    isCaseBuyersModalOpen: boolean;
    isCaseNotesModalOpen: boolean;
    isCasePolicyModalOpen: boolean;
    isEditCaseModalOpen: boolean;
    notes: Note[] | undefined;
    selectedAction: Action | undefined;
    selectedBuyer: Buyer | undefined;
    selectedPolicy: Policy | undefined;
    setBuyers: (data: Buyer[]) => void;
    setCaseActions: (data: Action[]) => void;
    setCaseDetail: (data: Case) => void;
    setCasePolicies: (data: Policy[]) => void;
    setCompanyOptions: (data: DropdownItemProps[]) => void;
    setExcelLoading: (data: boolean) => void;
    setIsBidModalOpen: (data: boolean) => void;
    setIsCaseActionsModalOpen: (data: boolean) => void;
    setIsCaseBuyersModalOpen: (data: boolean) => void;
    setIsEditCaseModalOpen: (data: boolean) => void;
    setIsCaseNotesModalOpen: (data: boolean) => void;
    setIsCasePolicyModalOpen: (data: boolean) => void;
    setNotes: (data: Note[]) => void;
    setSelectedAction: (data: Action | undefined) => void;
    setSelectedBuyer: (data: Buyer | undefined) => void;
    setSelectedPolicy: (data: Policy | undefined) => void;
    setStatuses: (data: Status[]) => void;
    setStatusOptions: (data: DropdownItemProps[]) => void;
    setTradingCaseFiles: (data: TradingCaseFile[]) => void;
    setTradingCaseFileTypeOptions: (data: DropdownItemProps[]) => void;
    setTradingQueueData: (data: TradingQueueRow[] | undefined) => void;
    statuses: Status[] | undefined;
    statusOptions: DropdownItemProps[];
    tradingCaseFiles: TradingCaseFile[] | undefined;
    tradingCaseFileTypeOptions: DropdownItemProps[] | undefined;
    tradingQueueData: TradingQueueRow[] | undefined;
}

export const TradingQueueContext = createContext<TradingQueueContextProps>({
    buyers: undefined,
    caseActions: undefined,
    caseDetail: undefined,
    casePolicies: undefined,
    companyOptions: undefined,
    excelLoading: false,
    isBidModalOpen: false,
    isCaseActionsModalOpen: false,
    isCaseBuyersModalOpen: false,
    isCaseNotesModalOpen: false,
    isCasePolicyModalOpen: false,
    isEditCaseModalOpen: false,
    notes: undefined,
    selectedAction: undefined,
    selectedBuyer: undefined,
    selectedPolicy: undefined,
    setBuyers: notImplemented,
    setCaseActions: notImplemented,
    setCaseDetail: notImplemented,
    setCasePolicies: notImplemented,
    setCompanyOptions: notImplemented,
    setExcelLoading: notImplemented,
    setIsBidModalOpen: notImplemented,
    setIsCaseActionsModalOpen: notImplemented,
    setIsCaseBuyersModalOpen: notImplemented,
    setIsCaseNotesModalOpen: notImplemented,
    setIsCasePolicyModalOpen: notImplemented,
    setIsEditCaseModalOpen: notImplemented,
    setNotes: notImplemented,
    setSelectedAction: notImplemented,
    setSelectedBuyer: notImplemented,
    setSelectedPolicy: notImplemented,
    setStatuses: notImplemented,
    setStatusOptions: notImplemented,
    setTradingCaseFiles: notImplemented,
    setTradingCaseFileTypeOptions: notImplemented,
    setTradingQueueData: notImplemented,
    statuses: undefined,
    statusOptions: [],
    tradingCaseFiles: undefined,
    tradingCaseFileTypeOptions: [],
    tradingQueueData: undefined
});

const TradingQueueContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [buyers, setBuyers] = useState<Buyer[] | undefined>();
    const [caseActions, setCaseActions] = useState<Action[] | undefined>();
    const [caseDetail, setCaseDetail] = useState<Case | undefined>();
    const [casePolicies, setCasePolicies] = useState<Policy[] | undefined>();
    const [companyOptions, setCompanyOptions] = useState<DropdownItemProps[] | undefined>();
    const [excelLoading, setExcelLoading] = useState<boolean>(false);
    const [isBidModalOpen, setIsBidModalOpen] = useState<boolean>(false);
    const [isCaseActionsModalOpen, setIsCaseActionsModalOpen] = useState<boolean>(false);
    const [isCaseBuyersModalOpen, setIsCaseBuyersModalOpen] = useState<boolean>(false);
    const [isCaseNotesModalOpen, setIsCaseNotesModalOpen] = useState<boolean>(false);
    const [isCasePolicyModalOpen, setIsCasePolicyModalOpen] = useState<boolean>(false);
    const [isEditCaseModalOpen, setIsEditCaseModalOpen] = useState<boolean>(false);
    const [notes, setNotes] = useState<Note[] | undefined>();
    const [selectedAction, setSelectedAction] = useState<Action | undefined>();
    const [selectedBuyer, setSelectedBuyer] = useState<Buyer | undefined>();
    const [selectedPolicy, setSelectedPolicy] = useState<Policy | undefined>();
    const [statuses, setStatuses] = useState<Status[] | undefined>();
    const [statusOptions, setStatusOptions] = useState<DropdownItemProps[]>([]);
    const [tradingCaseFiles, setTradingCaseFiles] = useState<TradingCaseFile[] | undefined>();
    const [tradingCaseFileTypeOptions, setTradingCaseFileTypeOptions] = useState<DropdownItemProps[] | undefined>();
    const [tradingQueueData, setTradingQueueData] = useState<TradingQueueRow[] | undefined>();

    return (
        <TradingQueueContext.Provider
            value={{
                buyers,
                caseActions,
                caseDetail,
                casePolicies,
                companyOptions,
                excelLoading,
                isBidModalOpen,
                isCaseActionsModalOpen,
                isCaseBuyersModalOpen,
                isCaseNotesModalOpen,
                isCasePolicyModalOpen,
                isEditCaseModalOpen,
                notes,
                selectedAction,
                selectedBuyer,
                selectedPolicy,
                setBuyers,
                setCaseActions,
                setCaseDetail,
                setCasePolicies,
                setCompanyOptions,
                setExcelLoading,
                setIsBidModalOpen,
                setIsCaseActionsModalOpen,
                setIsCaseBuyersModalOpen,
                setIsCaseNotesModalOpen,
                setIsCasePolicyModalOpen,
                setIsEditCaseModalOpen,
                setNotes,
                setSelectedAction,
                setSelectedBuyer,
                setSelectedPolicy,
                setStatuses,
                setStatusOptions,
                setTradingCaseFiles,
                setTradingCaseFileTypeOptions,
                setTradingQueueData,
                statuses,
                statusOptions,
                tradingCaseFiles,
                tradingCaseFileTypeOptions,
                tradingQueueData
            }}
        >
            {children}
        </TradingQueueContext.Provider>
    );
};

export default TradingQueueContextProvider;
